import * as z from 'zod';

import { contactLinkerWithPhoneNumberQuerySchema } from '@/_pages/contacts/shared/schemas/contact-query-linker-with-phone-numbers-schema';
import { emailHistoryQuerySchema } from '@/_pages/settings/email-history/shared/schemas/email-history-query-schema';
import { currencyInput } from '@/_schemas/generic';
import { paginatedResponseSchema } from '@/_schemas/generic/paginated-response';

import {
  invoiceAgreementDeliverableQuerySchema,
  invoiceAgreementLinkQuerySchema,
  invoiceAgreementQuerySchema,
  invoiceLatestEmailHistorySchema,
  priceFrequencyQuerySchema,
} from './invoice-query-agreement-link-schema';
import { invoiceQueryLinkerExtendedSchema } from './invoice-query-schema-extended';

import { arPaymentCollectionQuerySchema } from '../invoice-payments/payment-transaction-query-schema';

export {
  invoiceAgreementDeliverableQuerySchema,
  invoiceAgreementLinkQuerySchema,
  invoiceAgreementQuerySchema,
  invoiceLatestEmailHistorySchema,
  priceFrequencyQuerySchema,
};
export type {
  InvoiceAgreementDeliverableQuerySchema,
  InvoiceAgreementLinkQuerySchema,
  InvoiceAgreementQuerySchema,
  InvoiceLatestEmailHistorySchema,
  PriceFrequencyQuerySchema,
} from './invoice-query-agreement-link-schema';

export const invoiceAccountReceivablePaymentQuerySchema = z.object({
  id: z.number(),
  account_receivable_id: z.number(),
  amount_applied: currencyInput.querySchema(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  deleted_at: z.string().nullable().nullish(),
  payment_transaction_id: z.number().nullable().nullish(),
  ar_payment_collection_id: z.number(),
  ar_payment_collection: arPaymentCollectionQuerySchema,
});

export type InvoiceAccountReceivablePaymentQuerySchema = z.infer<typeof invoiceAccountReceivablePaymentQuerySchema>;

/** `account_receivables` embedded on invoice detail. */
export const invoiceAccountReceivableQuerySchema = z.object({
  id: z.number(),
  invoice_id: z.number(),
  amount_original: currencyInput.querySchema(),
  balance_due: currencyInput.querySchema(),
  total_paid: currencyInput.querySchema(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  deleted_at: z.string().nullable().nullish(),
  account_receivable_payments: z.array(invoiceAccountReceivablePaymentQuerySchema).nullish(),
});

export type InvoiceAccountReceivableQuerySchema = z.infer<typeof invoiceAccountReceivableQuerySchema>;

export const invoiceQuerySchema = z.object({
  ...invoiceQueryLinkerExtendedSchema.shape,

  agreement_links: z.array(invoiceAgreementLinkQuerySchema).nullish(),

  billing_contact: contactLinkerWithPhoneNumberQuerySchema.nullable().nullish(),
  billing_email_contacts: z.array(contactLinkerWithPhoneNumberQuerySchema).nullish(),

  /** When included on list/detail — used for email column badge without extra fetch. */
  email_histories: z.array(emailHistoryQuerySchema).nullish(),

  /** Included by default on list; detail requires `latest_email_history` include. */
  latest_email_history: invoiceLatestEmailHistorySchema.nullable().optional(),

  /** When fetched with nested AR payment includes (e.g. `ar_payment_collection.payment_transactions...`). */
  account_receivables: invoiceAccountReceivableQuerySchema.nullish(),
});

export type InvoiceQuerySchema = z.infer<typeof invoiceQuerySchema>;

export const invoiceQueryAllSchema = paginatedResponseSchema(invoiceQuerySchema);

export type InvoiceQueryAllSchema = z.infer<typeof invoiceQueryAllSchema>;
